import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import {
  useDeleteOrganization,
  useDugoutOrganization,
  useOrganizationDeletionBlockers
} from '@/hooks/useDugoutOrganizations';
import { Button, CopyContent, Pill } from '@/ui';
import { Alert } from '@/ui/Alert';
import AuditLogLink from '@/ui/components/AuditLogLink';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import DataList from '@/ui/components/DataList/DataList';
import DataRow from '@/ui/components/DataList/DataRow';
import NotAuthorized from '@/ui/components/NotAuthorized';
import Spinner from '@/ui/components/Spinner';
import {
  hasPermission,
  ORGANIZATION_CHANGE,
  ORGANIZATION_DELETE,
  ORGANIZATION_VIEW
} from '@/utils/userPermission';

const formatDateTime = (value: string) => new Date(value).toLocaleString();

const OrganizationDetails = ({ organizationId }: { organizationId: string }) => {
  const navigate = useNavigate();
  const { permissions } = useContext(AppContext);
  const canChange = hasPermission({ permissions, requiredPermission: ORGANIZATION_CHANGE });
  const canDelete = hasPermission({ permissions, requiredPermission: ORGANIZATION_DELETE });

  const { data, isPending } = useDugoutOrganization(organizationId);
  const blockersQuery = useOrganizationDeletionBlockers(organizationId, canDelete);
  const blockers = blockersQuery.data ?? [];
  // Fail closed: until the blockers have loaded, Delete stays off rather than
  // treating "no answer yet" (or an error) as "nothing blocks it".
  const deleteAllowed = blockersQuery.isSuccess && blockers.length === 0;
  const deleteOrganization = useDeleteOrganization();

  const organization = data?.data;
  if (isPending) return <Spinner />;
  if (!organization) return <NotAuthorized heading="Not found" message="No such organization." />;

  const handleDelete = () => {
    if (!window.confirm(`Delete "${organization.name}"? This can be restored by an engineer.`)) {
      return;
    }
    deleteOrganization.mutate(organization.id, {
      onSuccess: () => navigate('..', { relative: 'path' })
    });
  };

  const [, ...otherEmails] = organization.emails;
  const referrerConfig = organization.referrer_config;

  return (
    <Container type="page">
      <div className="flex items-center justify-between mb-3">
        <PageHeader headingText={organization.name} />
        <div className="flex gap-2">
          {canChange && <Button onClick={() => navigate('edit')}>Edit</Button>}
          {canDelete && (
            <Button
              variant="outline"
              color="destructive"
              disabled={!deleteAllowed || deleteOrganization.isPending}
              onClick={handleDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {!organization.primary_email && (
        <Alert
          className="mt-3"
          message="No default email. ROIs can't be requested for this organization, and none will be generated automatically from its referrals, until one is added."
          variant="warning"
        />
      )}
      {organization.is_kustomer_managed && (
        <Alert
          className="mt-3"
          message="Synced from Kustomer. Kustomer's company sync can overwrite the name, visibility, internal owner, emails and auto-generate ROIs setting when the company changes there."
          variant="info"
        />
      )}

      <Card className="mt-3">
        <DataList>
          <DataRow label="Default email (ROI)" content={organization.primary_email ?? '—'} />
          <DataRow
            label="Other emails"
            content={otherEmails.length > 0 ? otherEmails.join(', ') : '—'}
          />
          <DataRow
            label="Phones"
            content={organization.phones.length > 0 ? organization.phones.join(', ') : '—'}
          />
          <DataRow label="Visibility">
            <Pill
              size="sm"
              variant="subtle"
              color={organization.is_visible_to_public ? 'primary' : 'neutral'}>
              {organization.is_visible_to_public ? 'Visible to public' : 'Hidden'}
            </Pill>
          </DataRow>
          <DataRow label="Internal owner" content={organization.internal_owner || '—'} />
          <DataRow label="Description" content={organization.description || '—'} />
          <DataRow label="Kustomer ID" content={organization.kustomer_id || '—'} />
          <DataRow label="Created" content={formatDateTime(organization.created_at)} />
          <DataRow label="Updated" content={formatDateTime(organization.updated_at)} />
          <DataRow label="History">
            <AuditLogLink entityType="organization" entityId={organization.id}>
              Audit log
            </AuditLogLink>
          </DataRow>
        </DataList>
      </Card>

      <Card className="mt-3">
        <h3 className="font-semibold mb-2">Referral code</h3>
        {referrerConfig ? (
          <DataList>
            <DataRow label="Code">
              <CopyContent content={referrerConfig.code}>
                <span className="font-mono">{referrerConfig.code}</span>
              </CopyContent>
            </DataRow>
            <DataRow label="Short link">
              {referrerConfig.short_url ? (
                <CopyContent content={referrerConfig.short_url}>
                  <a
                    href={referrerConfig.short_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-800 hover:underline">
                    {referrerConfig.short_url}
                  </a>
                </CopyContent>
              ) : (
                '—'
              )}
            </DataRow>
            <DataRow label="Clicks" content={String(referrerConfig.clicks ?? 0)} />
            <DataRow label="Status">
              <Pill
                size="sm"
                variant="subtle"
                color={referrerConfig.is_active ? 'primary' : 'neutral'}>
                {referrerConfig.is_active ? 'Active' : 'Inactive'}
              </Pill>
            </DataRow>
            <DataRow
              label="Auto-generate ROIs"
              tooltipContent="Referrals from this organization automatically request an ROI to its default email. Off while it has no email."
              content={
                !referrerConfig.should_auto_generate_roi
                  ? 'No'
                  : organization.primary_email
                    ? 'Yes'
                    : 'No (needs a default email)'
              }
            />
          </DataList>
        ) : (
          <p className="text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
            This organization has no referral code.
            {canChange && ' Issue one from Edit.'}
          </p>
        )}
      </Card>

      {canDelete && blockersQuery.isError && (
        <Alert className="mt-3" variant="error">
          <div className="flex items-center justify-between gap-2">
            <span>Couldn&apos;t check whether this organization can be deleted</span>
            <Button
              className="shrink-0"
              onClick={() => blockersQuery.refetch()}
              size="xs"
              variant="ghost">
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {canDelete && blockers.length > 0 && (
        <Card className="mt-3">
          <h3 className="font-semibold mb-2">Why this can&apos;t be deleted</h3>
          <ul className="list-disc pl-5 text-sm">
            {blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </Card>
      )}
    </Container>
  );
};

const OrganizationView = () => {
  const { organizationId } = useParams();
  const { permissions } = useContext(AppContext);

  if (!hasPermission({ permissions, requiredPermission: ORGANIZATION_VIEW })) {
    return <NotAuthorized />;
  }
  return <OrganizationDetails organizationId={organizationId as string} />;
};

export default OrganizationView;
